import React, { useState, useRef, useEffect, useCallback } from 'react';
import GreenPattern from '@/assets/Icons/green-pattern.svg';
import Overlay from '../Overlay';
import Image from 'next/image';
import { resolvePath } from '@/utils/resolvePath';
import { TAP_BTN_VIDEO_INTERACTION } from '@/analytics/events';
import { trackEventAPI } from '@/analytics/middleware';

interface Image {
    url: string;
    formats?: {
        thumbnail?: { url: string };
        small?: { url: string };
    };
}

interface VideoData {
    id: number;
    titleText: string;
    subText: string;
    videoUrl: string;
    thumbnailImage: { data: { attributes: Image } };
}
interface ImageCarouselProps {
    data: Array<VideoData>;
}

const GarageFeatureImageCarousel: React.FC<ImageCarouselProps> = ({ data }) => {
    const [showVideoPlayer, setShowVideoPlayer] = useState<boolean>(false);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0);
    const [muted, setMuted] = useState<boolean>(true);

    const handleImageClick = async (index: number) => {
        await trackEventAPI({
            event_name: TAP_BTN_VIDEO_INTERACTION,
            customAttributes: {
                video_name: data[index].titleText,
            },
            isFakeDoor: true,
        });
        setSelectedVideoIndex(index);
        setShowVideoPlayer(true);
    };

    return (
        <>
            <div className="title">
                {'Discover our '}
                <span>
                    premium
                    <GreenPattern />
                </span>
                {' car services'}
            </div>

            <div className="carousel-container">
                <div
                    className={`image-carousel ${showVideoPlayer ? 'hidden' : ''}`}
                >
                    {data.map((item, index) => (
                        <div
                            className="image-container"
                            onClick={() => handleImageClick(index)}
                        >
                            <img
                                key={item.id}
                                src={
                                    item.thumbnailImage.data?.attributes.formats
                                        ?.small?.url ||
                                    item.thumbnailImage.data.attributes.url
                                }
                                width={250}
                                height={346}
                                alt={`Thumbnail ${index + 1}`}
                                className="carousel-image"
                            />
                            <Overlay
                                title={item.titleText}
                                feature={item.subText}
                            />
                        </div>
                    ))}
                </div>

                {showVideoPlayer && (
                    <div
                        className={`video-player-container ${showVideoPlayer ? 'visible' : 'hidden'}`}
                    >
                        <MobileVideoPlayer
                            startIndex={selectedVideoIndex}
                            onClose={() => {
                                setMuted(true);
                                setShowVideoPlayer(false);
                            }}
                            key={selectedVideoIndex}
                            videos={data}
                            changeIndex={(index) =>
                                setSelectedVideoIndex(index)
                            }
                            muted={muted}
                            handleMute={(val) => setMuted(val)}
                        />
                    </div>
                )}
            </div>

            <style jsx>{`
                .title {
                    color: #000;
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 28px;
                    letter-spacing: -0.1px;
                    padding: 24px 16px 0;
                    & > span {
                        color: var(--button-hover-g-400, #0b753e);
                        display: inline-flex;
                        flex-direction: column;
                    }
                }
                .carousel-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    overflow: hidden;

                    //flex-direction: column;
                }
                .image-container {
                    border-radius: 16px;
                    background: var(--Surface-White, #fff);
                    box-shadow: 0px 4px 10px 4px rgba(54, 53, 76, 0.08);
                    position: relative;
                    overflow: hidden;
                    width: 250px;
                    height: 346px;

                    & > img {
                        border-radius: 16px;
                        background: var(--Surface-White, #fff);
                        box-shadow: 0px 4px 10px 4px rgba(54, 53, 76, 0.08);
                        position: 'relative';
                        overflow: hidden;
                    }
                }

                .image-carousel {
                    display: grid;
                    overflow-x: auto;
                    grid-auto-flow: column;
                    gap: 8px;
                    padding: 24px 16px;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                    ::-webkit-scrollbar {
                        display: none;
                    }
                }

                .image-carousel.hidden {
                    display: none;
                }

                .video-player-container {
                    display: none;
                }

                .video-player-container.visible {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 100%;
                    height: 100%;
                }
            `}</style>
        </>
    );
};

export default GarageFeatureImageCarousel;

interface MobileVideoPlayerProps {
    startIndex: number;
    onClose: () => void;
    videos: VideoData[];
    changeIndex: (index: number) => void;
    muted: boolean;
    handleMute: (val: boolean) => void;
}

const MobileVideoPlayer: React.FC<MobileVideoPlayerProps> = ({
    startIndex = 0,
    onClose,
    videos,
    changeIndex,
    muted,
    handleMute,
}) => {
    const lastPlaybackTime = useRef(0);

    const [currentVideoIndex, setCurrentVideoIndex] =
        useState<number>(startIndex);
    const [progress, setProgress] = useState<number>(0);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const handleVideoEnd = () => {
        if (currentVideoIndex < videos.length - 1) {
            setCurrentVideoIndex((prevIndex) => prevIndex + 1);
            setProgress(0);
            changeIndex(currentVideoIndex + 1);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const progressValue =
                (videoRef.current.currentTime / videoRef.current.duration) *
                100;
            setProgress(progressValue || 0);
        }
    };

    const toggleMute = (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent triggering parent event handlers
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            handleMute(videoRef.current.muted);
        }
    };

    const handlePrevVideo = (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent accidental triggering from child elements
        if (currentVideoIndex > 0) {
            setCurrentVideoIndex(currentVideoIndex - 1);
        }
    };

    const handleNextVideo = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (currentVideoIndex < videos.length - 1) {
            setCurrentVideoIndex(currentVideoIndex + 1);
        }
    };

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = muted;
            videoRef.current.play().catch(() => {});
        }
    }, [currentVideoIndex, muted]);
    return (
        <div className="video-container" onClick={toggleMute}>
            <div className="top-controls">
                <div className="progress-bar">
                    {videos.map((_, index) => (
                        <div key={index} className="progress-segment">
                            <div
                                className="progress-fill"
                                style={{
                                    width:
                                        index === currentVideoIndex
                                            ? `${progress}%`
                                            : index < currentVideoIndex
                                              ? '100%'
                                              : '0%',
                                }}
                            />
                        </div>
                    ))}
                </div>
                <button className="mute-button">
                    <Image
                        src={
                            muted
                                ? resolvePath('/images/seo/mute.svg')
                                : resolvePath('/images/seo/unmute.svg')
                        }
                        width={24}
                        height={24}
                        style={{ opacity: 0.6 }}
                    />
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="close-button"
                >
                    ✕
                </button>
            </div>
            <div className="video-wrapper">
                <div>
                    {/* <button
                        onClick={handlePrevVideo}
                        className="video-nav left"
                    /> */}
                    <video
                        ref={videoRef}
                        className="video-element"
                        onEnded={handleVideoEnd}
                        onTimeUpdate={handleTimeUpdate}
                        muted={muted}
                        playsInline
                    >
                        <source
                            src={videos[currentVideoIndex].videoUrl}
                            type="video/mp4"
                        />
                    </video>
                    {/* <button
                        onClick={handleNextVideo}
                        className="video-nav right"
                    /> */}
                </div>
                <Overlay
                    title={videos[currentVideoIndex].titleText}
                    feature={videos[currentVideoIndex].subText}
                />

                {/* <div className="overlay">
                    HONDA CITY
                    <div className="brand-feature">Exterior detailing</div>
                </div> */}
            </div>
            <style jsx>{`
                .video-container {
                    width: 100%;
                    height: 100vh;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: black;
                    position: fixed;
                    top: 0;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 9999;
                }
                .top-controls {
                    position: absolute;
                    top: 25px;
                    left: 10px;
                    right: 10px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px;
                    z-index: 10;
                }
                .mute-button,
                .close-button {
                    color: white;
                    font-size: 20px;
                    background: none;
                    border: none;
                    cursor: pointer;
                }
                .progress-bar {
                    display: flex;
                    flex: 1;
                    margin: 0 10px;
                    position: absolute;
                    top: -5px;
                    left: 0;
                    right: 0;
                    z-index: 10;
                }
                .progress-segment {
                    flex: 1;
                    height: 4px;
                    background: #fff;
                    margin: 0 2px;
                    border-radius: 2px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    background: #37c87e;
                }
                .video-wrapper {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: relative;
                }
                .video-element {
                    width: 100%;
                    object-fit: cover;
                }
                .overlay {
                    position: absolute;
                    bottom: 20px;
                    left: 10px;
                    right: 10px;
                    color: white;
                    padding: 20px;
                    border-radius: 10px;
                    text-align: center;
                    display: flex;
                    gap: 12px;
                    color: var(--Text-White, #fff);
                    flex-direction: column;
                    align-items: flex-start;
                    font-size: 18px;
                    font-weight: 600;
                    line-height: 24px;
                    &::after {
                        content: '';
                        width: 114px;
                        height: 1px;
                        background: linear-gradient(
                            90deg,
                            #36c47c 0%,
                            rgba(54, 196, 124, 0) 100%
                        );
                        position: relative;
                        top: -45px;
                    }
                }

                .brand-feature {
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 24px;
                }
            `}</style>
        </div>
    );
};
